// DisqualificationReason.js
import { Box, Text, Button, Layer, Image, Header, Spinner, grommet, Grommet } from "grommet";
import { useState, useEffect, useContext, useRef } from "react";
import { useFetch } from "use-http";
import { UserContext } from "../../App";
import { getDBURL, correctTimeZone, updateStateHandler, generateSyncId, usePopupFocus } from "../Utils/Utils";
import useTrapFocus from "../Utils/useTrapFocus";
import CheckBoxGroupWithText from './CheckBoxGroupWithText';
import Notes from "./Notes";
import DangerPopupWindow from "./DangerPopupWindow";
import WarningPopupWindow from "../Base components/WarningPopupWindow";
import ConfirmPopupWindow from "./ConfirmPopupWindow";
import { useErrorLogger } from './Logger';

// Component to handle disqualification reason for a seedling
const DisqualificationReason = (props) => {
    const {plant, station, treatment, updatePlantStatus, reRenderSourseScreen, onClose, disqualYear, disable} = props;
    // console.log('disqualYear  >>  ', props.disqualYear);
    // console.log("In DisqualificationReason props >>  ", props);
    const currentUser = useContext(UserContext);
    const { get, post } = useFetch(getDBURL());
    const { DBlogError } = useErrorLogger();

    // State variables
    const [valuesLoaded, setValuesLoaded] = useState(false);
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [showDangerPopup, setShowDangerPopup] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');

    // Checkboxes states
    const [bunchScale, setBunchScale] = useState([]);
    const [berryScale, setBerryScale] = useState([]);
    const [notRelevant, setNotRelevant] = useState([]);
    const [disqualificationReasons, setDisqualificationReasons] = useState(null);

    // Tracks if the user has made any edits
    const [hasEdit, setHasEdit] = useState(false);
    // Ref to store the previous disqualification reasons for change detection
    const prevDisqualificationReasons = useRef(null);

    // Ref for the modal and focus trap to keep focus within it  
    const modalRef = useRef(null);
    useTrapFocus(modalRef, true); // Trap focus inside the modal while it's open

    // Manage focus when opening modal windows
    usePopupFocus(showDangerPopup, "danger-msg");
    usePopupFocus(showConfirmPopup, "confirm-msg");
    usePopupFocus(showWarning, "warning-msg");

    

    // Load disqualification reason from database when component mounts
    useEffect(() => {
        let isMounted = true;
        const loadData = async () => {
            if (isMounted) {
                try {
                    const reasons = await loadDisqualReason();
                    if (reasons) {
                        setDisqualificationReasons(reasons);
                        setValuesLoaded(true);

                        // Initialize checkboxes based on fetched data
                        setBunchScale(initializeCheckboxes(reasons).bunchScale);
                        setBerryScale(initializeCheckboxes(reasons).berryScale);
                        setNotRelevant(initializeCheckboxes(reasons).notRelevant);
                    }
                } catch (error) {
                    console.error(' 🔴 Failed to load disqualification reasons >> ', error.message);
                    await DBlogError({
                        error,
                        parameters: JSON.stringify({ seedlingId: plant.Id }),
                        userName: currentUser.email,
                        moduleName: 'DisqualificationReason',
                        functionName: 'loadData',
                        description: 'Failed to load disqualification reasons',
                    });
                }
            }
        };
        loadData();
        return () => { isMounted = false; }; 
    }, []);

    // Detect and update `hasEdit` state when disqualificationReasons change
    useEffect(() => {
        if (prevDisqualificationReasons.current &&
            JSON.stringify(prevDisqualificationReasons.current) !== JSON.stringify(disqualificationReasons)) {
            setHasEdit(true);
            setBunchScale(initializeCheckboxes(disqualificationReasons).bunchScale);
            setBerryScale(initializeCheckboxes(disqualificationReasons).berryScale);
            setNotRelevant(initializeCheckboxes(disqualificationReasons).notRelevant);
        }
        prevDisqualificationReasons.current = disqualificationReasons;
    }, [disqualificationReasons]);

    // Function to fetch disqualification reasons from database
    async function loadDisqualReason() {
        try {
            let SQLparams = null;
            if (plant) {
                SQLparams = {
                    SeedlingId: plant.Id,
                    TreatmentId: null,
                    StationId: null,
                    Year: disqualYear ? disqualYear : new Date().getFullYear(),
                }
            } else {
                SQLparams = {
                    SeedlingId: null,
                    TreatmentId: treatment.treatmentID,
                    StationId: station.Id,
                    Year: disqualYear ? disqualYear : new Date().getFullYear(),
                }
            }
            const response = await post('/getDisqualReason/dt=' + Date.now(), SQLparams);
            // console.log('In getDisqualReason response.result  >>  ', response.result);

            if (!response.success) {
                throw new Error(`HTTP error! ${response.message}`);
            }
            if (response?.success && response.result?.length) {
                // console.log('In getDisqualReason in if  >>  ', response.success);
                const reasons = response.result[0];
                return ({
                    Id: reasons.Id,
                    SeedlingId: reasons.SeedlingId,
                    TreatmentId: reasons.TreatmentId,
                    StationId: reasons.StationId,
                    Year: reasons.Year,
                    CreateDate: reasons.CreateDate,
                    CreatedBy: reasons.CreatedBy,
                    Rot: reasons.Rot,
                    LowFertility: reasons.LowFertility,
                    ShotBerries: reasons.ShotBerries,
                    DensePoorSet: reasons.DensePoorSet,
                    Other: reasons.Other,
                    Notes: reasons.Notes,
                    Seeded: reasons.Seeded,
                    SmallFruit: reasons.SmallFruit,
                    PoorColor: reasons.PoorColor,
                    PoorTaste: reasons.PoorTaste,
                    SoftFruit: reasons.SoftFruit,
                    NotRelevant: reasons.NotRelevant,
                    UpdatedAt: reasons.UpdatedAt,
                    UpdatedBy: reasons.UpdatedBy,
                    LastSyncDate: reasons.LastSyncDate,
                });
            }
        } catch (error) {
            console.error(' 🔴 Error in getDisqualReason >> ', error.message);
            await DBlogError({
                error,
                parameters: JSON.stringify({ seedlingId: plant.Id }),
                userName: currentUser.email,
                moduleName: 'DisqualificationReason',
                functionName: 'loadDisqualReason',
                description: 'Error occurred in loadDisqualReason function',
            });
        }
    };

    // Save disqualification reasons with error handling
    const saveDisqualifyReasons = async () => {
        try {
            const updatedReasons = {
                ...disqualificationReasons,
                ...(disqualificationReasons.Id ? {
                    UpdatedAt: correctTimeZone(new Date()),
                    UpdatedBy: currentUser.email,
                } : {
                    Id: generateSyncId(),
                    CreateDate: correctTimeZone(new Date()),
                    CreatedBy: currentUser.email,
                    Year: new Date().getFullYear(),
                }),
                LastSyncDate: correctTimeZone(new Date())
            };
            setDisqualificationReasons(updatedReasons);

            // Post to server using the updated object
            const response = await post(`/addOrUpdateDisqualReason/dt=${Date.now()}`, updatedReasons);
            if (!response.success) throw new Error(response.message);
            
            /*	Return Codes:
                RETURN 1 indicates a successful update.
                RETURN 2 indicates a successful insertion.
                RETURN 0 indicates a failure if an error occurs.
            **/

            // if (response?.success && response.result > 0
            return response.result;

        } catch (error) {
            console.error('🔴 Error in saveDisqualifyReasons >> ', error.message);
            await DBlogError({
                error,
                parameters: JSON.stringify({ disqualificationReasons }),
                userName: currentUser.email,
                moduleName: 'DisqualificationReason',
                functionName: 'saveDisqualifyReasons',
                description: 'Error occurred in saveDisqualifyReasons function',
            });
        }
    };

    // Function to disqualify the seedling
    const disqualify = async () => {
        try {
            const res = await saveDisqualifyReasons();
            // console.log('res >>  ', res);
            if (plant) {
                const seeded = berryScale.find(choice => choice.value === "Seeded").checked;

                // Prepare arguments for updateSeedlingStatus
                const updateArgs = seeded 
                ? [plant.Id, 3, seeded]   // Pass the third parameter if seeded is true
                : [plant.Id, 3];          // Omit the third parameter otherwise

                // The ...updateArgs spreads the array into individual arguments for updatePlantStatus.
                // Call updatePlantStatus with dynamic arguments
                await updatePlantStatus(...updateArgs);
            }

            if (res === 2) reRenderSourseScreen();
            setShowConfirmPopup(false);
            onClose();
        } catch (error) {
            console.error(' 🔴 Error in disqualify >> ', error.message);
        }
    };

    // Function to initialize checkboxes with data
    const initializeCheckboxes = (values) => {
        return {
            bunchScale: [
                { label: "Rot", value: "Rot", stateKey: 'Rot', checked: values.Rot },
                { label: "Low fertility", value: "LowFertility", stateKey: 'LowFertility', checked: values.LowFertility },
                { label: "Shot berries", value: "ShotBerries", stateKey: 'ShotBerries', checked: values.ShotBerries },
                { label: "Dense/Poor set", value: "DensePoorSet", stateKey: 'DensePoorSet', checked: values.DensePoorSet },
                { label: "Other/Describe:", value: "OtherDescribe", stateKey: 'Other', checked: values.Other },
            ],
            berryScale: [
                { label: "Seeded", value: "Seeded", stateKey: 'Seeded', checked: values.Seeded },
                { label: "Small fruit", value: "SmallFruit", stateKey: 'SmallFruit', checked: values.SmallFruit },
                { label: "Poor color", value: "PoorColor", stateKey: 'PoorColor', checked: values.PoorColor },
                { label: "Poor taste", value: "PoorTaste", stateKey: 'PoorTaste', checked: values.PoorTaste },
                { label: "Soft fruit", value: "SoftFruit", stateKey: 'SoftFruit', checked: values.SoftFruit },
            ],
            notRelevant: [
                { label: "Not relevant", value: "NotRelevant", stateKey: 'NotRelevant', checked: values.NotRelevant }
            ],
        };
    };

    // Check if "Other" checkbox is checked AND the "Notes" input is empty
    const validateOtherCheckbox = () => {
        const otherCheckbox = bunchScale.find(choice => choice.value === "OtherDescribe");
        if (otherCheckbox && otherCheckbox.checked && !disqualificationReasons.Notes) {
            setWarningMessage("Please enter a description in the 'Notes' field if you've selected 'Other/Describe'.");
            setShowWarning(true);
            return false; // Return false if "Other" is checked and "Notes" is empty
        }
        return true;
    };

    const onSave = async () => {
        // Check if "Other" checkbox is checked AND the "Notes" input is empty
        if (!validateOtherCheckbox()) return;
        console.log('disqualificationReasons  ::>>  ', disqualificationReasons);


       // if (plant.StatusID === 3) { // Check if the seedling is already disqualified
       if (disqualificationReasons.Id) { // Check if the seedling is already disqualified
            await disqualify();
            onClose();
        } else { // Disqualify the seedling if not already disqualified
            openDangerPopupWindow();
        }
    };

    // Function to open the danger popup window
    const openDangerPopupWindow = () => {
        // console.log('disqualificationReasons  ::>>  ', disqualificationReasons);
        // // not disqualified
        // if (hasEdit && plant.StatusID !== 3) {
        //     setShowDangerPopup(true);
        // } else {
        //     onClose(); // Close the window if there are no edits or the seedling is already disqualified
        // }
        setShowDangerPopup(true);
    };

    // Function to handle confirmation in the danger popup window
    const onDangerConfirm = async () => {
        await disqualify();
        setShowConfirmPopup(false);
    };

    // Function to handle window close
    const onWindowClose = () => {
        if (hasEdit) { // Show confirmation if there are unsaved edits
            setShowConfirmPopup(true);
        } else {
            onClose(); // Close the window if no edits were made
        }
    };

    // Function to handle confirmation popup confirmation
    const onConfirm = async () => {
        setShowConfirmPopup(false);
        
        // Check for empty "Notes" field if "Other" is checked
        if (!validateOtherCheckbox()) return;

        if (plant && plant.StatusID !== 3) {
            setShowDangerPopup(true); // Show danger popup if the seedling is not already disqualified
        } else {
            await disqualify();
            onClose();
        }
    };

    // Function to handle confirmation popup close
    const onCloseConfirmWindow = () => {
        setHasEdit(false); 
        setShowConfirmPopup(false);
        onClose();
    };

    // Function to handle warning popup close
    const onCloseWarning = () => {
        setWarningMessage("");
        setShowWarning(false);
    };

    // Helper function to check if "Other" checkbox is checked
    const otherCheckbox = bunchScale.find(choice => choice.value === "OtherDescribe");
    const isOtherChecked = otherCheckbox ? otherCheckbox.checked : false;

    return (
        <Layer
            id="disqual-reason"
            position="center"
            style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)', 
            }}
            onEsc={onClose}
            onClickOutside={() => { }}
        >
            <Box
                id="disqual-reas"
                ref={modalRef}
                tabIndex="-1"
                overflow='auto'
                pad="small"
                gap="small"
                width="450px"
                background="white"
                style={{
                    position: 'relative',
                    margin: 'auto', 
                }}
            >
                <Box
                    pad="xsmall"
                    background={{
                        image: "url('SeedlingIcon.png')", 
                        size: "contain",
                        repeat: "no-repeat",
                        opacity: 0.1, 
                    }}
                    border={{ color: "brand" }}
                >
                    <Box margin={{ vertical: "20px" }}>
                        <Text alignSelf="center" color="brand" size="24px"
                            style={{
                                background: "linear-gradient(to left, #00739D, #f9dd94 100%)",
                                backgroundPosition: "0 100%",
                                backgroundSize: "100% 3px",
                                backgroundRepeat: "repeat-x",
                                fontStyle: "italic"
                            }}
                        > <strong>Disqualification Reasons</strong></Text>
                    </Box>

                    <Box pad={{ horizontal: "xsmall" }} align="center" justify="center">
                    
                    {plant ? (
                    <>  
                        {/* Seedling data */}
                        <Text weight="bold" style={{ fontStyle: "italic", lineBreak: "strict",  padding: "0 0 10px 0" }}>
                            for Seedling
                        </Text>
                        <Box direction="row-responsive">
                            <Box direction="row">
                                <Text margin={{ horizontal: "xsmall" }} size="medium">year:</Text>
                                <Text weight="bold" margin={{ horizontal: "xsmall" }} size="medium">
                                    {plant.Year}
                                </Text>
                            </Box>
                            <Box direction="row">
                                <Text margin={{ horizontal: "xsmall" }} size="medium">row:</Text>
                                <Text weight="bold" margin={{ horizontal: "xsmall" }} size="medium">
                                    {plant.Row}
                                </Text>
                            </Box>
                            <Box direction="row">
                                <Text margin={{ horizontal: "xsmall" }} size="medium">col:</Text>
                                <Text weight="bold" margin={{ horizontal: "xsmall" }} size="medium">
                                    {plant.Column}
                                </Text>
                            </Box>
                        </Box>
                    </>
                    ) : (
                    <Box align="center" justify="center">  
                        {/* Station data */}
                        <Box align="center"  pad = "small" justify="center">
                            <Box direction="row" align="center">
                            <Text weight="bold" style={{ fontStyle: "italic" }}>
                                for{" "}
                                <span style={{ fontWeight: "bold", fontSize: "22px", color: '#FFAA15' }}>
                                    {treatment.treatmentName.toLowerCase().includes("treatment")
                                        ? treatment.treatmentName.replace(/treatment/i, "").trim()
                                        : treatment.treatmentName}
                                </span>
                                {" "}treatment
                            </Text>
                            </Box>

                            <Box direction="row" align="center">
                                <Text weight="bold"  style={{ ontStyle: "italic"}}>
                                    at station{" "}
                                    <span style={{ fontWeight: "bold", fontSize: "22px", color:'#FFAA15'}}>
                                        {station.Name}
                                    </span>
                                </Text> 
                            </Box>
                        </Box>

                        <Box background="neutral-3" height="1px" width="40%" margin={{vertical: "xsmall"}} > </Box>

                    <Box   pad = "small" align="center" justify="center">      
                        <Box  direction="row-responsive" gap="medium">
                        
                            <Box direction="row">
                                <Text margin={{ horizontal: "xsmall" }} size="medium">mother:</Text>
                                <Text weight="bold" margin={{ horizontal: "xsmall" }} size="medium">
                                    {station.Mother}
                                </Text>
                            </Box>
                            <Box direction="row">
                                <Text margin={{ horizontal: "xsmall" }} size="medium">father:</Text>
                                <Text weight="bold" margin={{ horizontal: "xsmall" }} size="medium">
                                    {station.Father}
                                </Text>
                            </Box>
                        </Box>
                        <Box direction="row" justify="center" >
                            <Text margin={{ horizontal: "xsmall" }} size="medium">rootstock:</Text>
                            <Text weight="bold" margin={{ horizontal: "xsmall" }} size="medium">
                                {station.Rootstock}
                            </Text>
                        </Box>
                        </Box>    
                    </Box>
                    )}
                    
                    <Box background="neutral-3" height="2px" width="90%" margin={{vertical: "xsmall"}} > </Box>
                    </Box>
                    
                    

                    {!valuesLoaded ? (
                        <Box align="center" pad="small" gap="small" round="full" margin={{ vertical: "large" }}>
                            <Spinner size="medium" /> <Text>Loading...</Text>
                        </Box>
                    ) : (
                        <Box direction="row-responsive">
                            <Box>
                                <CheckBoxGroupWithText
                                    stateHandler={setDisqualificationReasons}
                                    choices={bunchScale}
                                    title={'Bunch Scale:'}
                                    disable={disable}                                
                                    groupkey={'bunchScale'}
                                    numOfColumns={1}
                                />
                                <Box width="240px" height="100px">
                                    <Notes
                                        stateHandler={setDisqualificationReasons}
                                        disable={disable || !isOtherChecked}
                                        initValue={disqualificationReasons.Notes}
                                        fieldName="Notes"
                                    />
                                </Box>
                            </Box>
                            <Box>
                                <CheckBoxGroupWithText
                                    stateHandler={setDisqualificationReasons}
                                    choices={berryScale}
                                    title={'Berry Scale:'}
                                    disable={disable}                                    
                                    groupkey={'berryScale'}
                                    numOfColumns={1}
                                />
                                <CheckBoxGroupWithText
                                    stateHandler={setDisqualificationReasons}
                                    choices={notRelevant}
                                    title={''}
                                    groupkey={'notRelevant'}
                                    numOfColumns={1}
                                    disable={disable}
                                />
                            </Box>
                        </Box>
                    )}

                    <Box
                        as="footer"
                        gap="small"
                        direction="row"
                        align="center"
                        justify="center"
                        pad={{ top: 'medium', bottom: 'small' }}
                    >
                        {valuesLoaded &&
                            <Button
                                disabled={disable}  
                                label={
                                    <Text color="white">
                                        <strong> Save </strong>
                                    </Text>
                                }
                                onClick={onSave}
                                primary
                            />}
                        <Button
                            label={
                                <Text color="brand">
                                    <strong>Close</strong>
                                </Text>
                            }
                            onClick={onWindowClose}
                            secondary
                        />
                    </Box>

                    {showDangerPopup && (
                        <DangerPopupWindow
                            msg={`The ${plant ? "seedling" : "treatment"} will be disqualified! Are you sure you want to continue?`}
                            onClose={() => {setShowDangerPopup(false); onClose();}}
                            onConfirm={onDangerConfirm}
                        />
                    )}

                    {showConfirmPopup && (
                        <ConfirmPopupWindow msg={"There are unsaved changes to the disqualification reasons. Would you like to save them before closing?"}
                            onClose={onCloseConfirmWindow}
                            onConfirm={onConfirm}
                        />
                    )}

                    {showWarning && (
                        <WarningPopupWindow msg={warningMessage} close={onCloseWarning} />
                    )}

                </Box>
            </Box>
        </Layer>
    );
};

export default DisqualificationReason;